// client/src/components/templates/recibos/TemplateReciboPreview.js - VERSÃO COMPLETA
import React, { useState, useRef, useEffect } from 'react';
import { FileText, Download, Printer, Eye, EyeOff, AlertCircle, Loader2, ArrowLeft, Thermometer, XCircle } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

const TemplateReciboPreview = ({ template, onClose }) => {
    const { setAlert } = useAlert();
    const [showPreview, setShowPreview] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [printMode, setPrintMode] = useState('a4');
    const [thermalHeight, setThermalHeight] = useState('250mm');
    const iframeRef = useRef(null);

    // Função para criar conteúdo de erro
    const createErrorContent = () => {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Recibo: ${template?.nome || 'N/A'}</title>
                <style>
                    body { 
                        font-family: Arial, sans-serif; 
                        line-height: 1.6; 
                        margin: 0; 
                        padding: 40px;
                        color: #333;
                        background: #f9f9f9;
                    }
                    .error-container { 
                        background: white;
                        border: 1px solid #e0e0e0;
                        border-radius: 8px;
                        padding: 40px;
                        margin: 50px auto;
                        max-width: 600px;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    }
                    .error-title { 
                        color: #d32f2f; 
                        font-size: 24px; 
                        margin-bottom: 20px;
                        text-align: center;
                    }
                    .error-message {
                        text-align: center;
                        margin-bottom: 30px;
                        color: #666;
                    }
                </style>
            </head>
            <body>
                <div class="error-container">
                    <h1 class="error-title">Recibo Sem Conteúdo</h1>
                    <p class="error-message">Este recibo não possui conteúdo para visualização.</p>
                </div>
            </body>
            </html>
        `;
    };

    // Função para processar o conteúdo e substituir placeholders CORRETOS
    const processContent = (content) => {
        if (!content) return '';
        
        console.log('📝 Processando conteúdo:', content);
        
        // ✅ CORREÇÃO: lista de placeholders de amostra sincronizada com
        // server/utils/receiptUtils.js (getReceiptPlaceholders/populateReceiptTemplate)
        // — {{empresa.endereco}} não existe mais no backend desde a migração
        // para {{empresa.unidade}}; esta prévia ainda usava o nome antigo,
        // então quem editava o template via esse preview nunca via esse
        // placeholder funcionar de verdade.
        let processedContent = content
            .replace(/\{\{empresa\.nome\}\}/g, '<strong>MINHA EMPRESA LTDA</strong>')
            .replace(/\{\{empresa\.cnpj\}\}/g, '12.345.678/0001-90')
            .replace(/\{\{empresa\.telefone\}\}/g, '(11) 9999-8888')
            .replace(/\{\{empresa\.email\}\}/g, 'contato@minhaempresa.com.br')
            .replace(/\{\{empresa\.unidade\}\}/g, 'Rua Exemplo, 123 - Centro - São Paulo/SP')
            .replace(/\{\{empresa\.logoUrl\}\}/g, '')
            .replace(/\{\{cliente\.nome\}\}/g, '<strong>JOÃO DA SILVA</strong>')
            .replace(/\{\{cliente\.email\}\}/g, 'joao@email.com')
            .replace(/\{\{cliente\.telefone\}\}/g, '(11) 98888-7777')
            .replace(/\{\{pagamento\.valor\}\}/g, '<strong style="font-size: 11px; display: block; text-align: center; margin: 5px 0;">R$ 1.500,00</strong>')
            .replace(/\{\{pagamento\.referencia\}\}/g, 'Consulta Médica')
            .replace(/\{\{pagamento\.data\}\}/g, '15/03/2024')
            .replace(/\{\{pagamento\.forma\}\}/g, 'Cartão de Crédito')
            .replace(/\{\{agendamento\.id\}\}/g, '65f0a1b2c3d4e5f6a7b8c9d0')
            .replace(/\{\{agendamento\.dataInicio\}\}/g, '10/03/2024')
            .replace(/\{\{agendamento\.horaInicio\}\}/g, '14:00')
            .replace(/\{\{agendamento\.dataFim\}\}/g, '10/03/2024')
            .replace(/\{\{agendamento\.horaFim\}\}/g, '15:00')
            .replace(/\{\{agendamento\.status\}\}/g, 'Concluído')
            .replace(/\{\{agendamento\.unidade\}\}/g, 'Unidade Centro')
            .replace(/\{\{agendamento\.profissional\}\}/g, 'Dr. Carlos Silva')
            .replace(/\{\{agendamento\.valorTotalItensFormatado\}\}/g, 'R$ 350,00')
            .replace(/\{\{servicosTable\}\}/g, `
                <div style="margin: 5px 0;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 9px; margin: 3px 0;">
                        <tr style="background: #f0f0f0;">
                            <th style="border: 1px solid #000; padding: 2px; text-align: left;">Serviço</th>
                            <th style="border: 1px solid #000; padding: 2px; text-align: center;">Valor</th>
                        </tr>
                        <tr>
                            <td style="border: 1px solid #000; padding: 2px;">Consulta Médica</td>
                            <td style="border: 1px solid #000; padding: 2px; text-align: center;">R$ 150,00</td>
                        </tr>
                        <tr>
                            <td style="border: 1px solid #000; padding: 2px;">Exames Laboratoriais</td>
                            <td style="border: 1px solid #000; padding: 2px; text-align: center;">R$ 200,00</td>
                        </tr>
                    </table>
                </div>
            `)
            // ✅ CORREÇÃO: faltavam os atalhos de item (servico/recurso/
            // itemVenda/curso/pacote) — existem em getReceiptPlaceholders()/
            // populateReceiptTemplate() no backend e o painel de variáveis
            // já oferece pra inserir, mas a prévia local nunca substituía.
            .replace(/\{\{servico\.nome\}\}/g, 'Consulta Médica')
            .replace(/\{\{servico\.categoria\}\}/g, 'Consultas')
            .replace(/\{\{servico\.descricao\}\}/g, 'Avaliação clínica geral')
            .replace(/\{\{recurso\.nome\}\}/g, 'Sala 1')
            .replace(/\{\{recurso\.categoria\}\}/g, 'Salas')
            .replace(/\{\{recurso\.tipo\}\}/g, 'Sala')
            .replace(/\{\{itemVenda\.nome\}\}/g, 'Protetor Solar FPS 50')
            .replace(/\{\{itemVenda\.descricao\}\}/g, 'Produto de venda avulsa')
            .replace(/\{\{curso\.nome\}\}/g, 'Curso de Primeiros Socorros')
            .replace(/\{\{curso\.cargaHoraria\}\}/g, '8h')
            .replace(/\{\{pacote\.nome\}\}/g, 'Pacote 10 Sessões')
            .replace(/\{\{dataEmissao\}\}/g, new Date().toLocaleDateString('pt-BR'));

        console.log('✅ Conteúdo processado:', processedContent);
        return processedContent;
    };

    // Função para criar conteúdo térmico
    const createThermalContent = () => {
        if (!template?.conteudo) {
            return createErrorContent();
        }

        const processedContent = processContent(template.conteudo);

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Recibo: ${template.nome}</title>
                <style>
                    * {
                        margin: 0 !important;
                        padding: 0 !important;
                        box-sizing: border-box !important;
                        line-height: 1.1 !important;
                    }
                    
                    html, body {
          width: 74mm !important;
          min-height: 50mm !important;
          height: auto !important;
          background: white !important;
          margin: 0 auto !important;
          padding: 2mm !important;
          font-family: 'Courier New', Courier, monospace !important;
          font-size: 9px !important;
          color: #000000 !important;
          overflow: visible !important;
        }
                    
                    .recibo-container {
          width: 70mm !important;
          min-height: auto !important;
          height: auto !important;
          padding: 1mm 0 !important;
          margin: 0 auto !important;
          overflow: visible !important;
          border-top: 1px dashed #ccc !important;
          border-bottom: 1px dashed #ccc !important;
        }
                    
                    .recibo-container * {
          margin: 0.5px 0 !important;
          padding: 0 !important;
          font-size: 9px !important;
          line-height: 1.1 !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          word-break: break-word !important;
          max-width: 70mm !important;
          font-family: 'Courier New', Courier, monospace !important;
          overflow: visible !important;
        }
                    
                    .recibo-container h1, 
                    .recibo-container h2, 
                    .recibo-container h3 {
                        margin: 4px 0 3px 0 !important;
                        font-size: 11px !important;
                        font-weight: bold !important;
                        text-align: center !important;
                        text-transform: uppercase !important;
                    }
                    
                    .recibo-container strong {
                        font-weight: bold !important;
                    }
                    
                    .recibo-container table {
                        width: 100% !important;
                        max-width: 74mm !important;
                        border-collapse: collapse !important;
                        margin: 4px 0 !important;
                        font-size: 9px !important;
                        table-layout: fixed !important;
                    }
                    
                    .recibo-container td, 
                    .recibo-container th {
                        padding: 2px 3px !important;
                        border: 1px solid #000 !important;
                        word-wrap: break-word !important;
                        overflow-wrap: break-word !important;
                        word-break: break-word !important;
                    }
                    
                    .recibo-container hr {
                        border: none !important;
                        border-top: 1px dashed #ccc !important;
                        margin: 4px 0 !important;
                    }
                </style>
            </head>
            <body>
                <div class="recibo-container">
                    ${processedContent}
                </div>
                
                <script>
                    function calculateAndSendHeight() {
                        try {
                            const container = document.querySelector('.recibo-container');
                            const body = document.body;
                            
                            if (container && body) {
                                const containerHeight = container.scrollHeight;
                                const bodyHeight = body.scrollHeight;
                                const contentHeight = Math.max(containerHeight, bodyHeight);
                                
                                const heightWithMargin = contentHeight + 40;
                                
                                if (window.parent) {
                                    window.parent.postMessage({
                                        type: 'thermalContentHeight',
                                        height: heightWithMargin
                                    }, '*');
                                }
                            }
                        } catch (error) {
                            if (window.parent) {
                                window.parent.postMessage({
                                    type: 'thermalContentHeight',
                                    height: 400
                                }, '*');
                            }
                        }
                    }
                    
                    document.addEventListener('DOMContentLoaded', function() {
                        calculateAndSendHeight();
                    });
                    
                    window.addEventListener('load', function() {
                        setTimeout(calculateAndSendHeight, 100);
                    });
                    
                    setTimeout(calculateAndSendHeight, 500);
                </script>
            </body>
            </html>
        `;
    };

    // Função para criar conteúdo A4
    const createA4Content = () => {
        if (!template?.conteudo) {
            return createErrorContent();
        }

        const processedContent = processContent(template.conteudo);

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Recibo: ${template.nome}</title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 20mm;
                    }
                    
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    
                    body {
                        font-family: Arial, Helvetica, sans-serif;
                        color: #222;
                        line-height: 1.4;
                        margin: 0;
                        padding: 0;
                        background: white;
                        width: 170mm;
                        height: 257mm;
                        overflow: hidden;
                    }
                    
                    .recibo-container {
                        border: 2px solid #0b63d6;
                        padding: 15mm;
                        width: 140mm;
                        height: 227mm;
                        background: white;
                        position: relative;
                        overflow: hidden;
                    }
                    
                    .recibo-container * {
                        max-width: 140mm !important;
                        word-wrap: break-word !important;
                    }
                    
                    .recibo-container h1 {
                        font-size: 18px;
                        margin: 10px 0;
                        text-align: center;
                    }
                    
                    .recibo-container h2 {
                        font-size: 16px;
                        margin: 8px 0;
                    }
                    
                    .recibo-container p {
                        margin: 5px 0;
                        font-size: 12px;
                    }
                    
                    .recibo-container table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 10px 0;
                        font-size: 11px;
                    }
                    
                    .recibo-container td, 
                    .recibo-container th {
                        padding: 4px 6px;
                        border: 1px solid #ccc;
                        word-wrap: break-word;
                    }
                </style>
            </head>
            <body>
                <div class="recibo-container">
                    ${processedContent}
                </div>
            </body>
            </html>
        `;
    };

    // Função unificada para criar conteúdo
    const createPageContent = () => {
        return printMode === 'termica' ? createThermalContent() : createA4Content();
    };

    // FUNÇÃO DE IMPRESSÃO CORRIGIDA
    const createPrintContent = () => {
        if (!template?.conteudo) {
            return createErrorContent();
        }

        const processedContent = processContent(template.conteudo);

        if (printMode === 'termica') {
            return `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>Recibo: ${template.nome}</title>
                    <style>
                        @media print {
                            @page {
                                size: 80mm auto;
                                margin: 0;
                                padding: 0;
                            }
                            
                            html, body {
                                width: 80mm !important;
                                min-height: 100mm !important;
                                height: auto !important;
                                margin: 0 auto !important;
                                padding: 2mm !important;
                                font-family: 'Courier New', Courier, monospace !important;
                                font-size: 10px !important;
                                color: #000000 !important;
                                background: white !important;
                                overflow: hidden !important;
                            }
                            
                            .recibo-container {
                                width: 76mm !important;
                                min-height: auto !important;
                                height: auto !important;
                                margin: 0 auto !important;
                                padding: 1mm 0 !important;
                                overflow: hidden !important;
                            }
                            
                            .recibo-container * {
                                margin: 0.5px 0 !important;
                                padding: 0 !important;
                                font-size: 10px !important;
                                line-height: 1.1 !important;
                                word-wrap: break-word !important;
                                overflow-wrap: break-word !important;
                                word-break: break-word !important;
                                max-width: 76mm !important;
                                font-family: 'Courier New', Courier, monospace !important;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="recibo-container">
                        ${processedContent}
                    </div>
                    
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() {
                                    window.close();
                                }, 500);
                            }, 250);
                        };
                    </script>
                </body>
                </html>
            `;
        } else {
            return `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>Recibo: ${template.nome}</title>
                    <style>
                        @media print {
                            @page {
                                size: A4 portrait;
                                margin: 20mm;
                            }
                            
                            body {
                                font-family: Arial, Helvetica, sans-serif;
                                color: #222;
                                line-height: 1.4;
                                margin: 0;
                                padding: 0;
                                background: white;
                                width: 170mm;
                                height: auto;
                                min-height: 257mm;
                            }
                            
                            .recibo-container {
                                border: 2px solid #0b63d6;
                                padding: 15mm;
                                width: 140mm;
                                min-height: 227mm;
                                background: white;
                                margin: 0 auto;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="recibo-container">
                        ${processedContent}
                    </div>
                    
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() {
                                    window.close();
                                }, 500);
                            }, 250);
                        };
                    </script>
                </body>
                </html>
            `;
        }
    };

    // Efeito para lidar com mensagens de altura do iframe térmico
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data.type === 'thermalContentHeight' && printMode === 'termica') {
                const contentHeight = event.data.height;
                const heightInMM = (contentHeight * 0.264583) + 20;
                const safeHeight = Math.max(heightInMM, 150);
                setThermalHeight(`${safeHeight}mm`);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [printMode]);

    // Resetar altura quando mudar o modo
    useEffect(() => {
        setThermalHeight('250mm');
    }, [printMode]);

    // Função para recarregar o iframe
    const reloadIframe = () => {
        if (iframeRef.current && template?.conteudo) {
            setIsLoading(true);
            const iframe = iframeRef.current;
            iframe.srcDoc = createPageContent();
            
            setTimeout(() => {
                setIsLoading(false);
            }, 2000);
        }
    };

    // Recarregar quando o modo ou template mudar
    useEffect(() => {
        reloadIframe();
    }, [printMode, template]);

    // FUNÇÃO DE IMPRESSÃO
    const handlePrint = () => {
        setIsLoading(true);
        
        try {
            const printContent = createPrintContent();
            const printWindow = window.open('', '_blank', 'width=800,height=600');
            
            if (!printWindow) {
                setAlert('Não foi possível abrir a janela de impressão. Verifique se o pop-up está bloqueado.', 'warning');
                setIsLoading(false);
                return;
            }
            
            printWindow.document.write(printContent);
            printWindow.document.close();
            
            const fallbackPrint = setTimeout(() => {
                if (!printWindow.closed) {
                    printWindow.print();
                }
            }, 2000);
            
            const checkClosed = setInterval(() => {
                if (printWindow.closed) {
                    clearInterval(checkClosed);
                    clearTimeout(fallbackPrint);
                    setIsLoading(false);
                }
            }, 500);
            
        } catch (error) {
            console.error('❌ Erro na impressão:', error);
            setAlert('Erro ao preparar impressão: ' + error.message, 'danger');
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        setIsLoading(true);
        const content = createPrintContent();
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recibo-${template.nome}-${printMode}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsLoading(false);
    };

    const togglePreview = () => {
        setShowPreview(!showPreview);
    };

    const togglePrintMode = () => {
        const newMode = printMode === 'a4' ? 'termica' : 'a4';
        setPrintMode(newMode);
    };

    if (!template) {
        return (
            <div className="content-panel">
                <div className="panel-state-wrapper">
                    <FileText size={48} className="text-gray-400" />
                    <p>Nenhum recibo selecionado para visualização</p>
                </div>
            </div>
        );
    }

    return (
        <div className="content-panel">
            {/* CABEÇALHO NO ESTILO DO FORMULÁRIO */}
            <div className="form-header">
                <h3 className="form-title">Visualizando: {template.nome}</h3>
                <button 
                    type="button" 
                    onClick={onClose} 
                    title="Fechar" 
                    className="form-close-button"
                >
                    <XCircle size={24} />
                </button>
            </div>

            {/* RESUMO DO RECIBO */}
            <fieldset className="form-section">
                <legend className="form-section-title">Resumo do Recibo</legend>
                <div className="form-grid-container-auto-fit">
                    <div className="form-group">
                        <label className="form-label">Nome:</label>
                        <div className="form-input form-input-disabled">{template.nome}</div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Status:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${template.ativo ? 'badge-green' : 'badge-gray'}`}>
                                {template.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Caracteres:</label>
                        <div className="form-input form-input-disabled highlight">
                            {template.conteudo?.length || 0}
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Modo:</label>
                        <div className="form-input form-input-disabled">
                            <span className={`badge ${printMode === 'a4' ? 'badge-blue' : 'badge-orange'}`}>
                                {printMode === 'a4' ? 'A4' : 'Térmica'}
                            </span>
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Páginas:</label>
                        <div className="form-input form-input-disabled highlight">1</div>
                    </div>
                </div>
            </fieldset>

            {/* CONTROLES DE VISUALIZAÇÃO */}
            <fieldset className="form-section">
                <legend className="form-section-title">Controles de Visualização</legend>
                <div className="editor-toolbar">
                    <button 
                        onClick={togglePrintMode}
                        className="action-button action-button-secondary"
                        disabled={isLoading}
                    >
                        <Thermometer size={16} />
                        {printMode === 'a4' ? 'Modo Térmica' : 'Modo A4'}
                    </button>

                    <button 
                        onClick={togglePreview}
                        className="action-button action-button-secondary"
                        disabled={isLoading}
                    >
                        {showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showPreview ? 'Ocultar Preview' : 'Mostrar Preview'}
                    </button>
                    
                    <button 
                        onClick={handleDownload}
                        className="action-button action-button-secondary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        Download
                    </button>
                    
                    <button 
                        onClick={handlePrint}
                        className="action-button action-button-primary"
                        disabled={isLoading || !template.conteudo}
                    >
                        {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                        Imprimir
                    </button>
                </div>
            </fieldset>

            {/* PREVIEW DO CONTEÚDO */}
            {showPreview && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        Preview do Recibo - Modo {printMode === 'a4' ? 'A4' : 'Térmica'}
                    </legend>
                    
                    {isLoading ? (
                        <div className="panel-state-wrapper">
                            <Loader2 size={48} className="animate-spin text-blue-500" />
                            <p>Carregando recibo...</p>
                        </div>
                    ) : !template.conteudo ? (
                        <div className="panel-state-wrapper error">
                            <AlertCircle size={48} className="text-yellow-500" />
                            <h4>Recibo Sem Conteúdo</h4>
                            <p>Este recibo não possui conteúdo para visualização.</p>
                        </div>
                    ) : printMode === 'termica' ? (
                        <div className="thermal-preview-wrapper">
                            <div className="thermal-content-wrapper">
                                <div className="thermal-iframe-container">
                                    <iframe
                                        ref={iframeRef}
                                        srcDoc={createPageContent()}
                                        title={`Recibo: ${template.nome} (Térmica)`}
                                        sandbox="allow-same-origin"
                                        style={{ 
                                            height: thermalHeight,
                                            minHeight: '150mm',
                                            maxHeight: '500mm',
                                            border: '1px solid #e0e0e0'
                                        }}
                                        onLoad={() => setIsLoading(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="a4-preview-wrapper">
                            <div className="single-page-container">
                                <div className="a4-page-single">
                                    <iframe
                                        ref={iframeRef}
                                        srcDoc={createPageContent()}
                                        title={`Recibo: ${template.nome} (A4)`}
                                        sandbox="allow-same-origin"
                                        className="preview-iframe-single"
                                        onLoad={() => setIsLoading(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </fieldset>
            )}

            {/* BOTÕES DE AÇÃO NO FINAL */}
            <div className="form-actions">
                <button 
                    type="button" 
                    onClick={onClose}
                    className="action-button action-button-secondary"
                >
                    <ArrowLeft size={18} />
                    Voltar para Lista
                </button>
            </div>
        </div>
    );
};

export default TemplateReciboPreview;